import {AppManifest} from '@wix/editor-platform-sdk-types'
import {AppDescriptor, EditorSDK, PageSettingsTabType} from '@wix/platform-editor-sdk'
import {
  EVENTS_APP_ID,
  EVENTS_WEB_API_BASE,
  EVENT_LIST_SECTION_ID,
  ExperimentNames,
  SCHEDULE_SECTION_ID,
} from '@wix/wix-events-commons-statics'
import {AppManifestBuilder} from '@wix/yoshi-flow-editor'
import {
  APP_ACTION_ADD_EVENT_ELEMENTS,
  APP_ACTION_CREATE_EVENT,
  APP_ACTION_MANAGE_EVENTS,
  APP_ACTION_OPEN_EVENT_PAGES,
  EVENT_DELETE_EVENTS,
  EVENT_MANAGE_EVENTS,
} from '../events'
import {SdkWrapper} from '../services/sdk'
import {TFunction} from '../services/translations'
import {FlowApi} from '../types'
import {widgetGfppBuilder} from './gfpp'
import {membersPageBuilder} from './members-page'
import {detailsPageSlotsBuilder} from './slots'

export const getAppManifestFactory =
  (
    getT: () => TFunction,
    getLocale: () => string,
    getToken: () => string,
    getEditorSdk: () => EditorSDK,
    getFlowApi: () => FlowApi,
    isResponsive: () => boolean,
  ) =>
  async ({appManifestBuilder}: {appManifestBuilder: AppManifestBuilder}): Promise<AppManifest> => {
    const t = getT()
    const locale = getLocale()
    const token = getToken()
    const editorSdk = getEditorSdk()
    const responsive = isResponsive()
    const flowApi = getFlowApi()
    const sdkWrapper = SdkWrapper(editorSdk, token)
    const eventsListSeoPanelEnabled = flowApi.experiments.enabled(ExperimentNames.EventsListSeoPanel)

    const appData = await sdkWrapper.getAppData(EVENTS_APP_ID)
    const instance = appData?.instance

    const appDescriptor: AppDescriptor = {
      mainActions: [
        {
          title: t('appManagerManageEvents'),
          actionId: APP_ACTION_MANAGE_EVENTS,
          icon: 'appManager_settingsAction',
        },
      ],
      customActions: [
        {
          title: t('appManagerCreateEvent'),
          type: 'dashboard',
          icon: 'appManager_settingsAction',
          actionId: APP_ACTION_CREATE_EVENT,
        },
        {
          title: t('appManagerOpenEventPages'),
          type: 'editorActions',
          icon: 'appManager_pagesAction',
          actionId: APP_ACTION_OPEN_EVENT_PAGES,
        },
        {
          title: t('appManagerAddEventElements'),
          type: 'editorActions',
          icon: 'appManager_addElementsAction',
          actionId: APP_ACTION_ADD_EVENT_ELEMENTS,
        },
      ],
      defaultActions: {
        upgrade: {
          upgradeType: 'SITE_UPGRADE',
          upgradeText: t('appManagerUpgradeText'),
          upgradeLinkText: t('appManagerUpgradeLinkText'),
        },
        learnMoreKB: '2f8a99f2-0e47-4c5f-bc17-4a768db57c8f',
      },
    }

    const manifest: AppManifest = {
      appDescriptor,
      pages: {
        applicationSettings: {
          default: {
            displayName: t('pagesPanelEventsSectionTitle'),
            helpId: '9e9821b7-9e29-4ca0-ad92-0bd08c1d15ec',
          },
        },
        applicationActions: {
          default: [
            {
              title: t('pagesPanelEventsSectionManage'),
              event: EVENT_MANAGE_EVENTS,
              icon: 'settingsAction',
            },
            {
              title: t('pagesPanelEventsSectionDelete'),
              event: EVENT_DELETE_EVENTS,
              icon: 'deleteRadio',
            },
          ],
        },
        pageActions: {
          default: [],
          [EVENT_LIST_SECTION_ID]: [],
          [SCHEDULE_SECTION_ID]: [],
        },
        pageSettings: {
          default: [
            {
              title: t('pagesPanelEventsDetailsTabPageInfoTitle'),
              url: `https://editor.wixapps.net${EVENTS_WEB_API_BASE}/html/page-panel-info?locale=${locale}&instance=${instance}`,
              type: 'page_info' as PageSettingsTabType,
              helpId: '9e9821b7-9e29-4ca0-ad92-0bd08c1d15ec',
            },
            {
              title: t('pagesPanelEventsDetailsTabLayoutsTitle'),
              type: 'layout' as PageSettingsTabType,
              helpId: 'ecea98ce-90c7-48e6-ab40-207d6cc2758d',
              url: '',
            },
            {
              title: t('pagesPanelEventsDetailsTabPermissionsTitle'),
              type: 'permissions' as PageSettingsTabType,
              helpId: responsive ? 'beb7ca82-41b0-443c-89b5-1534d0398568' : 'b893d6b5-13b3-44d0-9e11-46eecd862a87',
              url: '',
            },
          ],
          [EVENT_LIST_SECTION_ID]: [
            {
              title: t('pagesPanelEventsDetailsTabPageInfoTitle'),
              type: 'page_info' as PageSettingsTabType,
              helpId: '9e9821b7-9e29-4ca0-ad92-0bd08c1d15ec',
              url: '',
            },
            {
              title: t('pagesPanelEventsDetailsTabLayoutsTitle'),
              type: 'layout' as PageSettingsTabType,
              helpId: 'ecea98ce-90c7-48e6-ab40-207d6cc2758d',
              url: '',
            },
            {
              title: t('pagesPanelEventsDetailsTabPermissionsTitle'),
              type: 'permissions' as PageSettingsTabType,
              helpId: responsive ? 'beb7ca82-41b0-443c-89b5-1534d0398568' : 'b893d6b5-13b3-44d0-9e11-46eecd862a87',
              url: '',
            },
            eventsListSeoPanelEnabled
              ? {
                  type: 'seo' as PageSettingsTabType,
                  title: t('pagesPanelSeoTitle'),
                }
              : null,
          ].filter(Boolean),
          [SCHEDULE_SECTION_ID]: [
            {
              title: t('pagesPanelEventsDetailsTabPageInfoTitle'),
              url: `https://editor.wixapps.net${EVENTS_WEB_API_BASE}/html/page-panel-info?locale=${locale}&instance=${instance}&page-type=schedule`,
              type: 'page_info' as PageSettingsTabType,
              helpId: '9e9821b7-9e29-4ca0-ad92-0bd08c1d15ec',
            },
            {
              title: t('pagesPanelEventsDetailsTabLayoutsTitle'),
              type: 'layout' as PageSettingsTabType,
              helpId: 'ecea98ce-90c7-48e6-ab40-207d6cc2758d',
              url: '',
            },
            {
              title: t('pagesPanelEventsDetailsTabPermissionsTitle'),
              type: 'permissions' as PageSettingsTabType,
              helpId: responsive ? 'beb7ca82-41b0-443c-89b5-1534d0398568' : 'b893d6b5-13b3-44d0-9e11-46eecd862a87',
              url: '',
            },
          ],
        },
        pageDescriptors: {
          default: {
            icon: 'tpaPageType',
          },
          [EVENT_LIST_SECTION_ID]: {
            icon: 'tpaPageType',
          },
          [SCHEDULE_SECTION_ID]: {
            icon: 'tpaPageType',
          },
        },
      },
    } as AppManifest

    appManifestBuilder = appManifestBuilder.withJsonManifest(manifest)

    appManifestBuilder = membersPageBuilder(appManifestBuilder)

    if (!responsive) {
      appManifestBuilder = await widgetGfppBuilder({t, editorSdk, token})(appManifestBuilder)
    }

    if (flowApi.experiments.enabled(ExperimentNames.EventDetailsSlots)) {
      appManifestBuilder = await detailsPageSlotsBuilder({t})(appManifestBuilder)
    }

    return appManifestBuilder.build()
  }
